import { IconButton, Stack, Typography } from '@mui/material';
import { AvailableRelationGroupFilters, Group } from '@schooly/api';
import { FilterButton, SearchInput } from '@schooly/components/filters';
import { useFlag } from '@schooly/hooks/use-flag';
import { useRenderGroupType } from '@schooly/hooks/use-render-group-type';
import { CrossIcon, FilterIcon, Loading } from '@schooly/style';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AddGroupsForRelationFiltersPopup } from '../../../components/common/AddGroupsForRelationExternalSidebar/AddGroupsForRelationFiltersPopup';
import { InlineFiltersTags } from '../../../components/common/AddGroupsForRelationExternalSidebar/InlineFiltersTags';
import { GroupCard } from '../../../components/common/GroupCard/GroupCard';
import { NoSearchResultsFound } from '../../../components/common/NoSearchResultsFound/NoSearchResultsFound';
import { ModalPanel } from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../components/uikit-components/Modal/ModalHeader';
import { EMPTY_FILTERS } from './context/useManageRelationGroup';

interface ManageGroupModalRightSidebarProps {
  onAddGroup: (group: Group) => void;
  currentYear: string;
  relatedGroups: Group[];
  isFetching: boolean;
  filterApplied?: boolean;
  searchQuery: string;
  onResetFilters?: () => void;
  onSetFilters: (filters: AvailableRelationGroupFilters) => void;
  handleSetFiltersQuery: (query: string) => void;
  filters: AvailableRelationGroupFilters;
}

export const ManageGroupModalRightSidebar: FC<ManageGroupModalRightSidebarProps> = ({
  onAddGroup,
  currentYear,
  relatedGroups,
  isFetching,
  filterApplied,
  searchQuery,
  onResetFilters,
  onSetFilters,
  filters,
  handleSetFiltersQuery,
}) => {
  const { $t } = useIntl();
  const [filtersModalVisible, openFilterModal, closeFiltersModal] = useFlag(false);

  const renderGroupType = useRenderGroupType();
  const renderList = () => {
    if (isFetching) {
      return (
        <div className="pt-3">
          <Loading />
        </div>
      );
    }

    return relatedGroups?.length ? (
      <Stack gap={1}>
        {relatedGroups.map((group) => {
          return (
            <GroupCard
              key={group.id}
              title={group.name}
              subjectName={renderGroupType(group)}
              subjectArchived={group.subject?.archived}
              onSelect={() => onAddGroup(group)}
              selectable
              withBorder
              id={group.id}
            />
          );
        })}
      </Stack>
    ) : (
      <NoSearchResultsFound type="small" />
    );
  };

  return (
    <Stack sx={{ height: '100%' }}>
      <ModalHeader
        title={
          <Stack flexDirection="row" gap={1}>
            <Typography variant="h1">
              <FormattedMessage id="profile-Groups" />
            </Typography>
            <Typography variant="h1" color="common.grey">
              {currentYear}
            </Typography>
          </Stack>
        }
        active
      />
      <ModalPanel sx={{ pt: 1, pb: 1 }} active>
        <Stack gap={1}>
          <SearchInput
            placeholder={$t(
              { id: 'groups-SearchAmongCount' },
              {
                GroupPlural: `${relatedGroups?.length || 0} ${$t({
                  id: 'assessments-Groups',
                }).toLowerCase()}`,
              },
            )}
            onChangeText={handleSetFiltersQuery}
            value={searchQuery}
            canClear={false}
            InputProps={{
              endAdornment: (
                <Stack pl={0.75} gap={0.5} pr={1} flexDirection="row">
                  {!!searchQuery && (
                    <IconButton
                      sx={(theme) => ({ '&:hover': { color: theme.palette.text.primary } })}
                      inverse
                      onClick={() => handleSetFiltersQuery('')}
                    >
                      <CrossIcon />
                    </IconButton>
                  )}
                  <FilterButton onClick={openFilterModal} hasFiltersApplied={!!filterApplied}>
                    <FilterIcon />
                  </FilterButton>
                </Stack>
              ),
            }}
          />
          <InlineFiltersTags
            onResetFilters={filterApplied ? onResetFilters : undefined}
            onOpenFilters={openFilterModal}
            filters={filters}
          />
        </Stack>
      </ModalPanel>

      <ModalPanel sx={{ pt: filterApplied ? 0 : 2.5, flex: '1 1 auto', overflowY: 'auto' }} active>
        {renderList()}
      </ModalPanel>

      {filtersModalVisible && (
        <AddGroupsForRelationFiltersPopup
          onClose={closeFiltersModal}
          defaultFilters={EMPTY_FILTERS}
          onSetFilters={onSetFilters}
          filters={filters}
        />
      )}
    </Stack>
  );
};
